// src/components/FeelingFeed.js
import React, { useEffect, useState, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  runTransaction,
  getCountFromServer,
} from "firebase/firestore";
import { getDeviceId } from "../utils/deviceId";

// ——— styles (unchanged from your file) ———
const feedWrap = {
  display: "flex",
  justifyContent: "center",
  width: "100%",
  marginTop: 16,
};

const feedCard = {
  width: "100%",
  maxWidth: 1050,
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const item = {
  background: "#ffffff",
  borderRadius: 18,
  padding: "16px 18px",
  boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
};

const msg = {
  fontSize: 20,
  fontStyle: "italic",
  color: "#2a1a52",
  whiteSpace: "pre-wrap",
};

const metaRow = {
  marginTop: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const time = {
  fontSize: 14,
  color: "#7a6b9a",
};

// reaction pill to match your theme
const pill = (on) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  border: "none",
  borderRadius: 999,
  padding: "6px 10px",
  cursor: "pointer",
  fontSize: 13,
  background: on ? "#ead9ff" : "#f5eaff",
  transition: "transform 120ms ease, background 120ms ease",
  pointerEvents: "auto",
});
const icon = { fontSize: 16, lineHeight: 1 };

// ——— time ago (unchanged logic) ———
function timeAgoFromTimestamp(ts) {
  let d;
  if (!ts) return "Just now";
  if (ts?.toDate) d = ts.toDate();
  else if (typeof ts?.seconds === "number")
    d = new Date(ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6));
  else d = new Date(ts);

  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "Just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

/* =========================================================
   Reactions (💜 🤗 🙌 🔥) — internal, no layout changes
   Stores per-reaction docs in subcollections:
   reactions_heart, reactions_hug, reactions_support, reactions_fire
   ========================================================= */
function ReactionPill({ feelingRef, type, emoji, label }) {
  const deviceId = useMemo(() => getDeviceId(), []);
  const [on, setOn] = useState(false);
  const [count, setCount] = useState(0);

  const subCol = useMemo(
    () => collection(feelingRef, `reactions_${type}`),
    [feelingRef, type]
  );
  const myDoc = useMemo(() => doc(subCol, deviceId), [subCol, deviceId]);

  async function refreshCount() {
    const snap = await getCountFromServer(subCol);
    setCount(snap.data().count || 0);
  }

  useEffect(() => {
    const unsub = onSnapshot(myDoc, (snap) => {
      setOn(snap.exists());
      refreshCount();
    });
    return () => unsub();
  }, [myDoc]); // eslint-disable-line

  const toggle = async () => {
    try {
      await runTransaction(db, async (tx) => {
        const me = await tx.get(myDoc);
        if (me.exists()) tx.delete(myDoc);
        else tx.set(myDoc, { label, createdAt: new Date() });
      });
      refreshCount();
    } catch (e) {
      console.error("Reaction toggle failed:", e);
    }
  };

  return (
    <button
      type="button"
      style={pill(on)}
      onClick={toggle}
      aria-label={label}
      title={label}
    >
      <span style={icon}>{emoji}</span>
      <span>{count}</span>
    </button>
  );
}

function ReactionBar({ feelingId }) {
  const feelingRef = useMemo(() => doc(db, "feelings", feelingId), [feelingId]);
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <ReactionPill feelingRef={feelingRef} type="heart" emoji="💜" label="Heart" />
      <ReactionPill feelingRef={feelingRef} type="hug" emoji="🤗" label="Hug" />
      <ReactionPill feelingRef={feelingRef} type="support" emoji="🙌" label="Support" />
      <ReactionPill feelingRef={feelingRef} type="fire" emoji="🔥" label="You got this" />
    </div>
  );
}

/* =========================
   Feed (unchanged otherwise)
   ========================= */
export default function FeelingFeed() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    // live updates; persists through refresh
    const q = query(collection(db, "feelings"), orderBy("timestamp", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const arr = [];
      snap.forEach((docSnap) => arr.push({ id: docSnap.id, ...docSnap.data() }));
      setItems(arr);
    });
    return () => unsub();
  }, []);

  return (
    <div style={feedWrap}>
      <div style={feedCard}>
        {items.map((f) => (
          <div key={f.id} style={item}>
            <div style={msg}>“{f.message}”</div>
            <div style={metaRow}>
              <div style={time}>⏰ {timeAgoFromTimestamp(f.timestamp)}</div>
              <ReactionBar feelingId={f.id} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
